import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DocumentWithStatus } from '@/types/document';
import { downloadFile } from '@/utils';
import Modal from '@/components/ui/Modal';
import {
  RiCloseLine,
  RiDownloadLine,
  RiFileTextLine,
  RiImageLine,
  RiFilePdfLine,
  RiFileWordLine,
  RiFileExcelLine,
  RiFilePptLine,
  RiFileUnknowLine,
  RiLoader4Line,
  RiVideoLine,
  RiVolumeUpLine,
} from 'react-icons/ri';

/**
 * Strip markdown code fences from text (for legacy summaries stored with code fences)
 */
function stripMarkdownCodeFence(text: string): string {
  if (!text) return text;
  let cleaned = text.trim();
  // Remove opening code fence: ```markdown, ```md, or just ```
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:markdown|md)?\s*/i, '');
  }
  // Remove closing code fence
  if (cleaned.endsWith('```')) {
    cleaned = cleaned.replace(/```\s*$/, '');
  }
  return cleaned.trim();
}

interface DocumentPreviewModalProps {
  document: DocumentWithStatus;
  isOpen: boolean;
  onClose: () => void;
  onDownload?: (document: DocumentWithStatus) => void;
}

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  document,
  isOpen,
  onClose,
  onDownload,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Internal download handler that uses our download utility
  const handleDownload = async () => {
    if (onDownload) {
      // Use the passed download handler if available
      onDownload(document);
    } else if (document.filePath) {
      // Otherwise use our internal download utility
      try {
        await downloadFile({
          fileName: document.fileName,
          filePath: document.filePath,
          fallbackToNewTab: true,
        });
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError(null);
    }
  }, [isOpen, document.id]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'd':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleDownload();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, document]);

  if (!isOpen) return null;

  const getFileExtension = (fileName: string): string => {
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  const getFileType = (fileName: string): string => {
    const ext = getFileExtension(fileName);

    // Images
    if (
      [
        'jpg',
        'jpeg',
        'png',
        'gif',
        'bmp',
        'webp',
        'svg',
        'tiff',
        'ico',
      ].includes(ext)
    ) {
      return 'image';
    }

    // PDFs (explicit extension)
    if (ext === 'pdf') {
      return 'pdf';
    }

    // Text files
    if (
      [
        'txt',
        'md',
        'csv',
        'json',
        'xml',
        'html',
        'css',
        'js',
        'ts',
        'log',
        'rtf',
      ].includes(ext)
    ) {
      return 'text';
    }

    // Office documents
    if (['doc', 'docx', 'odt'].includes(ext)) {
      return 'word';
    }

    if (['xls', 'xlsx', 'ods', 'csv'].includes(ext)) {
      return 'excel';
    }

    if (['ppt', 'pptx', 'odp'].includes(ext)) {
      return 'powerpoint';
    }

    // Video files (basic support)
    if (['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext)) {
      return 'video';
    }

    // Audio files (basic support)
    if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(ext)) {
      return 'audio';
    }

    // Default to PDF for unknown extensions (most documents in this system are PDFs)
    // This handles files with .a, .b, .c extensions or no extension
    return 'pdf';
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <RiImageLine className="w-5 h-5" />;
      case 'pdf':
        return <RiFilePdfLine className="w-5 h-5" />;
      case 'text':
        return <RiFileTextLine className="w-5 h-5" />;
      case 'word':
        return <RiFileWordLine className="w-5 h-5" />;
      case 'excel':
        return <RiFileExcelLine className="w-5 h-5" />;
      case 'powerpoint':
        return <RiFilePptLine className="w-5 h-5" />;
      case 'video':
        return <RiVideoLine className="w-5 h-5" />;
      case 'audio':
        return <RiVolumeUpLine className="w-5 h-5" />;
      default:
        return <RiFileUnknowLine className="w-5 h-5" />;
    }
  };

  const fileType = getFileType(document.fileName);
  const fileExtension = getFileExtension(document.fileName);

  const handleLoadComplete = () => {
    setIsLoading(false);
  };

  const handleLoadError = (errorMessage: string) => {
    setIsLoading(false);
    setError(errorMessage);
  };

  const renderPreviewContent = () => {
    if (!document.filePath) {
      return (
        <div className="flex items-center justify-center h-[70vh] text-gray-500">
          <div className="text-center">
            <RiFileUnknowLine className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>File path not available</p>
          </div>
        </div>
      );
    }

    switch (fileType) {
      case 'image':
        return (
          <div className="flex items-center justify-center min-h-[70vh] bg-gray-50 rounded-lg">
            <img
              src={document.filePath}
              alt={document.fileName}
              className="max-w-full max-h-full object-contain transition-transform duration-200"
              onLoad={handleLoadComplete}
              onError={() => handleLoadError('Failed to load image')}
            />
          </div>
        );

      case 'pdf':
        return (
          <div className="w-full min-h-[70vh] bg-gray-50 rounded-lg">
            {isLoading && (
              <div className="flex items-center justify-center h-[70vh]">
                <RiLoader4Line className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            )}
            <iframe
              src={document.filePath}
              className="w-full h-[70vh] border-0 rounded-lg"
              title={document.fileName}
              onLoad={handleLoadComplete}
              onError={() => handleLoadError('Failed to load PDF')}
            />
          </div>
        );

      case 'text':
        return (
          <div className="w-full min-h-[70vh]">
            {isLoading && (
              <div className="flex items-center justify-center h-[70vh]">
                <RiLoader4Line className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            )}
            <iframe
              src={document.filePath}
              className="w-full h-[70vh] border border-gray-200 rounded-lg bg-white"
              title={document.fileName}
              onLoad={handleLoadComplete}
              onError={() => handleLoadError('Failed to load text file')}
            />
          </div>
        );

      case 'word':
      case 'excel':
      case 'powerpoint':
        // For Office documents, we'll try Microsoft Office Online viewer first,
        // and fall back to Google Docs Viewer if that fails
        const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(document.filePath)}`;
        const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(document.filePath)}&embedded=true`;

        return (
          <div className="w-full min-h-[70vh]">
            {isLoading && (
              <div className="flex items-center justify-center h-[70vh]">
                <RiLoader4Line className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            )}
            <iframe
              src={error ? googleViewerUrl : officeViewerUrl}
              className="w-full h-[70vh] border border-gray-200 rounded-lg"
              title={document.fileName}
              onLoad={handleLoadComplete}
              onError={() => {
                if (!error) {
                  // Try Google Viewer as fallback
                  setError('office-failed');
                } else {
                  handleLoadError(
                    'Failed to load document. You can download it to view locally.'
                  );
                }
              }}
            />
            {error === 'office-failed' && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                Falling back to Google Docs Viewer...
              </div>
            )}
          </div>
        );

      case 'video':
        return (
          <div className="flex items-center justify-center min-h-[70vh] bg-gray-50 rounded-lg">
            <video
              src={document.filePath}
              controls
              className="max-w-full max-h-[70vh] rounded"
              onLoadedData={handleLoadComplete}
              onError={() => handleLoadError('Failed to load video')}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'audio':
        return (
          <div className="flex items-center justify-center min-h-96 bg-gray-50 rounded-lg">
            <div className="text-center">
              <RiVolumeUpLine className="w-16 h-16 mx-auto mb-6 text-gray-400" />
              <audio
                src={document.filePath}
                controls
                className="max-w-full"
                onLoadedData={handleLoadComplete}
                onError={() => handleLoadError('Failed to load audio')}
              >
                Your browser does not support the audio tag.
              </audio>
              <p className="mt-4 text-gray-600">{document.fileName}</p>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-[70vh] text-gray-500">
            <div className="text-center">
              {getFileIcon(fileType)}
              <p className="mt-4 text-lg font-medium">Preview not available</p>
              <p className="text-sm text-gray-400 mt-2">
                {fileExtension.toUpperCase()} files cannot be previewed
              </p>
              <p className="text-sm text-gray-400">
                Please download the file to view it
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" noPadding fullHeight>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
        <div className="flex items-center space-x-3 min-w-0">
          {getFileIcon(fileType)}
          <div className="min-w-0">
            <h3 className="text-lg font-medium text-gray-900 truncate">
              {document.fileName}
            </h3>
            <p className="text-sm text-gray-500 truncate">
              {fileExtension.toUpperCase()} •{' '}
              {Math.round((document.fileSize || 0) / 1024)} KB
              {isLoading && ' • Loading...'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Download button */}
          <button
            onClick={handleDownload}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Download"
          >
            <RiDownloadLine className="w-5 h-5" />
          </button>

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Two-column layout: Preview + Summary */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Preview */}
        <div className="flex-1 px-6 py-4 overflow-auto border-r border-gray-200">
          {error ? (
            <div className="flex items-center justify-center h-full text-red-500">
              <div className="text-center">
                <RiFileUnknowLine className="w-16 h-16 mx-auto mb-4 text-red-300" />
                <p className="text-lg font-medium">{error}</p>
                <button
                  onClick={handleDownload}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RiDownloadLine className="w-4 h-4 mr-2" />
                  Download File
                </button>
              </div>
            </div>
          ) : (
            renderPreviewContent()
          )}
        </div>

        {/* Right: Summary */}
        <div className="w-[400px] flex flex-col bg-gray-50">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
              Summary
            </h4>
          </div>
          <div className="flex-1 px-6 py-4 overflow-auto">
            {document.summary &&
            document.summary !== 'Summary not available' ? (
              <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-blue-600 [&_code]:before:content-none [&_code]:after:content-none [&_blockquote_p:first-of-type]:before:content-none [&_blockquote_p:last-of-type]:after:content-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {stripMarkdownCodeFence(document.summary)}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <RiFileTextLine className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No summary available</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentPreviewModal;
